import React, { useEffect, useState } from 'react';
import { Select, TextInput, Button, Grid, Paper, Group, Text, Loader, Collapse, MantineProvider,
  NumberInput, ActionIcon, Tooltip, 
  Pill, Notification,
  SegmentedControl, Drawer} from '@mantine/core';
import { 
  IconPlus, 
  IconTrash, 
  IconCheck, 
  IconChevronDown,
  IconChevronUp, 
  IconTrendingUp, 
  IconChartBar, 
  IconWallet, 
  IconClock, 
  IconCoin, 
  IconCurrencyDollar,
  IconEditCircle,
  IconSettings, 
  IconBan,
  IconDots,
  IconDownload,
  IconCopy
} from '@tabler/icons-react';
import InputWithTooltip from './InputWithTooltip';
import MinMaxInputPair from './MinMaxInputPair';
import CompactToggle from './CompactToggle';
import DurationRangeSlider from './DurationRangeSlider';
import ClickableHelpText from './ClickableHelpText';
import {
  deepPresetsClient,
  DeepExcelSettings,
  DeepWalletExportCreated,
  DeepWalletExportTask,
} from '../../api/deepPresetsClient';
import WinrateHistogramSlider from './WinrateHistogramSlider';
import ExcelStyleSettingsDrawer from './ExcelStyleSettingsDrawer';
import PeriodDaysExampleModal from './PeriodDaysExampleModal';
import TokenSolInvestExampleModal from './TokenSolInvestExampleModal';
import TradeTimeExampleModal, { TradeTimeExampleMode } from './TradeTimeExampleModal';
import TradeProvidersFilter, { TradeProvidersState } from './TradeProvidersFilter';
import PoolProvidersFilter, { PoolProvidersState } from './PoolProvidersFilter';
import PoolTypeProvidersFilter, { PoolTypeProviderRangeState } from './PoolTypeProvidersFilter';
import AggregatorsMapFilter, { AggregatorsMapRangeState } from './AggregatorsMapFilter';
import McapBucketFilter from './McapBucketFilter';
import TokenWalletExport from './TokenWalletExport';
import dayjs from 'dayjs';

interface Preset {
  id: string;
  name: string;
  format_type: 'EXCEL' | 'TXT_EXCEL' | 'TXT';
  is_active: boolean;
  is_active_preset: boolean;
  extended_analytics: boolean;
  min_winrate?: number;
  max_winrate?: number;
  pnl_min?: number;
  pnl_max?: number;
  min_roi?: number;
  max_roi?: number;
  min_median_mint_roi?: number;
  max_median_mint_roi?: number;
  min_avg_mint_roi?: number;
  max_avg_mint_roi?: number;
  usdt_min?: number;
  usdt_max?: number;
  usdc_min?: number;
  usdc_max?: number;
  wsol_min?: number;
  wsol_max?: number;
  sol_min?: number;
  sol_max?: number;
  total_sol_min?: number;
  total_sol_max?: number;
  total_usd_min?: number;
  total_usd_max?: number;
  total_tarded_mints_min?: number;
  total_tarded_mints_max?: number;
  last_trade_time_ago_min?: number;
  last_trade_time_ago?: number;
  median_mint_traid_duration_seconds_min?: number;
  median_mint_traid_duration_seconds_max?: number;
  sold_more_than_bought_trades_max?: number | null;
  sold_before_buy_mints_percent_max?: number | null;
  trade_providers?: TradeProvidersState;
  pool_providers?: PoolProvidersState;
  pool_type_providers?: PoolTypeProviderRangeState;
  aggregators_map?: AggregatorsMapRangeState;
  // --- NEW FIELDS ---
  period_days?: 1 | 7 | 14 | 30 | 45 | 90 | null;
  min_rocket_x2_count?: number | null;
  min_rocket_x5_count?: number | null;
  min_rocket_x10_count?: number | null;
  min_first_trade_time_ago?: number | null;
  max_first_trade_time_ago?: number | null;
  min_sol_token_invested?: number | null;
  // --- MCAP FIELDS ---
  min_avg_first_mcap_usd?: number | null;
  max_avg_first_mcap_usd?: number | null;
  min_avg_last_mcap_usd?: number | null;
  max_avg_last_mcap_usd?: number | null;
  min_median_first_mcap_usd?: number | null;
  max_median_first_mcap_usd?: number | null;
  min_median_last_mcap_usd?: number | null;
  max_median_last_mcap_usd?: number | null;
  // --- MCAP Bucket Filters ---
  min_max_first_token_percent_mcap?: Array<{
    min_mcap: number | null;
    max_mcap: number | null;
    min_percent: number | null;
    max_percent: number | null;
  }> | null;
  min_max_last_token_percent_mcap?: Array<{
    min_mcap: number | null;
    max_mcap: number | null;
    min_percent: number | null;
    max_percent: number | null;
  }> | null;

}

// --- helpers for duration string <-> seconds ---
function parseDurationStr(str: string): number | undefined {
  if (!str) return undefined;
  let total = 0;
  const regex = /(\d+)([dhms])/g;
  let match;
  while ((match = regex.exec(str)) !== null) {
    const num = parseInt(match[1], 10);
    switch (match[2]) {
      case 'd': total += num * 86400; break;
      case 'h': total += num * 3600; break;
      case 'm': total += num * 60; break;
      case 's': total += num; break;
    }
  }
  return total || undefined;
}
function formatDuration(seconds?: number | null): string {
  if (!seconds || isNaN(seconds)) return '';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [
    d ? `${d}d` : '',
    h ? `${h}h` : '',
    m ? `${m}m` : '',
    s ? `${s}s` : '',
  ].filter(Boolean).join('');
}
// --- end helpers ---

const TERMINAL_WALLET_EXPORT_STATUSES = new Set(['done', 'error', 'canceled']);

export const DeepPresets: React.FC = () => {
  // Telegram.WebApp.expand();

  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [notificationErrors, setNotificationErrors] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [winrateHistogram, setWinrateHistogram] = useState<number[] | null>(null);
  
  // Collapse states
  const [performanceExpanded, setPerformanceExpanded] = useState<boolean>(true);
  const [roiExpanded, setRoiExpanded] = useState<boolean>(false);
  const [balancesExpanded, setBalancesExpanded] = useState<boolean>(false);
  const [tradingExpanded, setTradingExpanded] = useState<boolean>(false);
  const [mcapExpanded, setMcapExpanded] = useState<boolean>(false);

  // Add state for detailed balance toggles
  const [solDetailedOpen, setSolDetailedOpen] = useState(false);
  const [usdDetailedOpen, setUsdDetailedOpen] = useState(false);

  // Excel settings drawer state
  const [excelDrawerOpened, setExcelDrawerOpened] = useState(false);
  const [excelSettings, setExcelSettings] = useState<DeepExcelSettings | null>(null);
  const [excelSettingsLoading, setExcelSettingsLoading] = useState(false);
  const [periodExampleOpened, setPeriodExampleOpened] = useState(false);
  const [tokenSolInvestExampleOpened, setTokenSolInvestExampleOpened] = useState(false);
  const [tradeTimeExampleMode, setTradeTimeExampleMode] = useState<TradeTimeExampleMode | null>(null);

  const userId = "example_user_id";

  // Enhanced mobile-first theme
  const theme = {
    background: {
      primary: '#07090E',
      secondary: '#161B22',
      tertiary: '#21262D',
      elevated: '#2D333B'
    },
    accent: {
      primary: '#4F46E5',
      secondary: '#7C3AED',
      tertiary: '#059669'
    },
    state: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B'
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#9CA3AF',
      disabled: '#6B7280'
    },
    border: {
      primary: '#374151',
      secondary: '#4B5563'
    },
    filterGradient: 'linear-gradient(180deg, #14171F 0%, #0C0E13 100%)', // updated
    sliderFill: '#01B773',
  };

  const modernStyles = {
    container: {
      minHeight: '100vh',
      background: theme.background.primary,
      color: theme.text.primary,
      padding: '12px',
      fontFamily: 'Space Grotesk, sans-serif'
    },
    card: {
      background: theme.background.secondary,
      borderRadius: '12px',
      border: `1px solid ${theme.border.primary}`,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(12px)',
    },
    collapseHeader: {
      padding: '10px 12px',
      background: theme.filterGradient, // updated
      borderRadius: '8px',
      borderLeft: `3px solid ${theme.accent.primary}`,
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: '6px',
      border: `1px solid ${theme.border.primary}`,
      '&:hover': {
        background: theme.background.tertiary,
        borderColor: theme.border.secondary
      }
    }
  };

  const validateFilters = () => {
    const newErrors: string[] = [];
    if (selectedPreset) {
      // Winrate
      if (
        selectedPreset.min_winrate != null &&
        selectedPreset.max_winrate != null &&
        selectedPreset.min_winrate > selectedPreset.max_winrate
      ) {
        newErrors.push(
          `<b>Performance: Winrate</b> — Min Winrate cannot be greater than Max Winrate.`
        );
      }
      // PNL
      if (
        selectedPreset.pnl_min != null &&
        selectedPreset.pnl_max != null &&
        selectedPreset.pnl_min > selectedPreset.pnl_max
      ) {
        newErrors.push(
          `<b>Performance: PNL</b> — PNL Min cannot be greater than PNL Max.`
        );
      }
      // ROI
      if (
        selectedPreset.min_roi != null &&
        selectedPreset.max_roi != null &&
        selectedPreset.min_roi > selectedPreset.max_roi
      ) {
        newErrors.push(
          `<b>ROI</b> — Min ROI cannot be greater than Max ROI.`
        );
      }
      // Median  ROI
      if (
        selectedPreset.min_median_mint_roi != null &&
        selectedPreset.max_median_mint_roi != null &&
        selectedPreset.min_median_mint_roi > selectedPreset.max_median_mint_roi
      ) {
        newErrors.push(
          `<b>Median ROI</b> — Min cannot be greater than Max.`
        );
      }
      // Avg  ROI
      if (
        selectedPreset.min_avg_mint_roi != null &&
        selectedPreset.max_avg_mint_roi != null &&
        selectedPreset.min_avg_mint_roi > selectedPreset.max_avg_mint_roi
      ) {
        newErrors.push(
          `<b>Avg ROI</b> — Min cannot be greater than Max.`
        );
      }
      // USDT
      if (
        selectedPreset.usdt_min != null &&
        selectedPreset.usdt_max != null &&
        selectedPreset.usdt_min > selectedPreset.usdt_max
      ) {
        newErrors.push(
          `<b>USDT</b> — Min cannot be greater than Max.`
        );
      }
      // USDC
      if (
        selectedPreset.usdc_min != null &&
        selectedPreset.usdc_max != null &&
        selectedPreset.usdc_min > selectedPreset.usdc_max
      ) {
        newErrors.push(
          `<b>USDC</b> — Min cannot be greater than Max.`
        );
      }
      // WSOL
      if (
        selectedPreset.wsol_min != null &&
        selectedPreset.wsol_max != null &&
        selectedPreset.wsol_min > selectedPreset.wsol_max
      ) {
        newErrors.push(
          `<b>WSOL</b> — Min cannot be greater than Max.`
        );
      }
      // SOL
      if (
        selectedPreset.sol_min != null &&
        selectedPreset.sol_max != null &&
        selectedPreset.sol_min > selectedPreset.sol_max
      ) {
        newErrors.push(
          `<b>SOL</b> — Min cannot be greater than Max.`
        );
      }
      // Total SOL
      if (
        selectedPreset.total_sol_min != null &&
        selectedPreset.total_sol_max != null &&
        selectedPreset.total_sol_min > selectedPreset.total_sol_max
      ) {
        newErrors.push(
          `<b>Total SOL</b> — Min cannot be greater than Max.`
        );
      }
      // Total USD
      if (
        selectedPreset.total_usd_min != null &&
        selectedPreset.total_usd_max != null &&
        selectedPreset.total_usd_min > selectedPreset.total_usd_max
      ) {
        newErrors.push(
          `<b>Total USD</b> — Min cannot be greater than Max.`
        );
      }
      // Total traded mints
      if (
        selectedPreset.total_tarded_mints_min != null &&
        selectedPreset.total_tarded_mints_max != null &&
        selectedPreset.total_tarded_mints_min > selectedPreset.total_tarded_mints_max
      ) {
        newErrors.push(
          `<b>Total Trades</b> — Min cannot be greater than Max.`
        );
      }
      // Median mint trade duration
      if (
        selectedPreset.median_mint_traid_duration_seconds_min != null &&
        selectedPreset.median_mint_traid_duration_seconds_max != null &&
        selectedPreset.median_mint_traid_duration_seconds_min > selectedPreset.median_mint_traid_duration_seconds_max
      ) {
        newErrors.push(
          `<b>Trade Duration</b> — Min cannot be greater than Max.`
        );
      }
    }
    setErrors(newErrors.map(e => e.replace(/<b>(.*?)<\/b>/g, '$1'))); // for legacy block
    setNotificationErrors(newErrors);
    return newErrors.length === 0;
  };

  const validateBalances = (preset: Preset) => {
    const errors: string[] = [];
    if (
      preset.sol_min &&
      preset.wsol_min &&
      preset.total_sol_max &&
      preset.sol_min + preset.wsol_min > preset.total_sol_max
    ) {
      errors.push(
        `<b>Balances: SOL/WSOL</b> — Sum of minimum SOL and WSOL cannot exceed total SOL maximum`
      );
    }
    return errors;
  };

  const fetchPresets = async () => {
    setIsLoading(true);
    try {
      const data = await deepPresetsClient.getPresets();
      // data: { id: preset, ... }
      const presetsArr = Object.entries(data).map(([id, preset]) => ({
        ...preset,
        id,
      }));
      setPresets(presetsArr);
      if (presetsArr.length > 0) {
        // Find active preset
        const activePreset = presetsArr.find(p => p.is_active_preset);
        if (activePreset) {
          setSelectedPresetId(activePreset.id);
          setSelectedPreset(activePreset);
        } else {
          setSelectedPresetId(presetsArr[0].id);
          setSelectedPreset(presetsArr[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching presets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewPreset = async () => {
    // Remove explicit type annotation, let TS infer
    const newPreset = {
      name: "New Preset",
      is_active: true,
      extended_analytics: false,
    };
    try {
      setIsLoading(true);
      const created = await deepPresetsClient.createPreset({ name: newPreset.name, is_active: true, extended_analytics: false });
      await fetchPresets();
      const newId = Object.keys(created)[0];
      setSelectedPresetId(newId);
      setSelectedPreset({ ...created[newId], id: newId });
    } catch (e) {
      // обработка ошибок
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicatePreset = async () => {
    if (!selectedPresetId || !selectedPreset) return;
    try {
      setIsLoading(true);
      const response = await deepPresetsClient.duplicatePreset(Number(selectedPresetId));
      await fetchPresets();
      const newId = String(response.id);
      setSelectedPresetId(newId);
      // Find the new preset and set it as selected
      const updatedPresets = await deepPresetsClient.getPresets();
      const presetsArr = Object.entries(updatedPresets).map(([id, preset]) => ({
        ...preset,
        id,
      }));
      const newPreset = presetsArr.find(p => p.id === newId);
      if (newPreset) {
        setSelectedPreset(newPreset);
      }
    } catch (error) {
      console.error("Error duplicating preset:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreset = async () => {
    if (!selectedPreset) return;
    const balanceErrors = validateBalances(selectedPreset);
    if (balanceErrors.length > 0) {
      setNotificationErrors(balanceErrors);
      setErrors(balanceErrors.map(e => e.replace(/<b>(.*?)<\/b>/g, '$1')));
      return;
    }
    if (!validateFilters()) return;
    setNotificationErrors([]); // clear on success
    setErrors([]);
    setSaveError(null);
    setIsLoading(true);
    try {
      let currentPresetId = selectedPresetId;
      if (selectedPresetId) {
        await deepPresetsClient.updatePreset(selectedPresetId, selectedPreset);
      } else {
        const created = await deepPresetsClient.createPreset(selectedPreset);
        currentPresetId = Object.keys(created)[0];
      }
      await fetchPresets();
      // Stay on the same preset after save
      if (currentPresetId) {
        setSelectedPresetId(currentPresetId);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setNotificationErrors([msg]);
      setSaveError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePreset = async (id: string | number) => {
    setIsLoading(true);
    try {
      await deepPresetsClient.deletePreset(id);
      await fetchPresets();
      setSelectedPresetId(null);
      setSelectedPreset(null);
    } catch (e) {
      // обработка ошибок
    } finally {
      setIsLoading(false);
    }
  };

  // --- Set active preset handler ---
  const handleSetActivePreset = async (id: string) => {
    setIsLoading(true);
    try {
      // Сначала деактивируем все, потом активируем нужный
      await Promise.all(
        presets.map(p =>
          deepPresetsClient.updatePreset(p.id, {
            ...p,
            is_active_preset: p.id === id,
          })
        )
      );
      await fetchPresets();
      setSelectedPresetId(id);
    } catch (e) {
      // обработка ошибок
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for export wallets
  const [exportLoading, setExportLoading] = useState(false);
  const [exportResult, setExportResult] = useState<string | null>(null);
  const [exportCreated, setExportCreated] = useState<DeepWalletExportCreated | null>(null);
  const [exportTask, setExportTask] = useState<DeepWalletExportTask | null>(null);
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);
  // Add state for storing the random icon positions
  const [iconPositions, setIconPositions] = useState<Array<{left: string, delay: string, size: number, type: number}>>([]);

  const handleExportWallets = async (tokens?: string[]): Promise<boolean> => {
    if (!selectedPresetId) return false;
    if (tokens && tokens.length > 5000) {
      setExportResult('Token exports accept no more than 5,000 unique mint addresses.');
      return false;
    }
    if (tokens && selectedPreset?.last_trade_time_ago == null && selectedPreset?.last_trade_time_ago_min == null) {
      setExportResult('Set Last trade time (minimum or maximum) in the preset before exporting from tokens.');
      return false;
    }
    setExportLoading(true);
    setExportResult(null);
    setExportCreated(null);
    setExportTask(null);
    setExportTaskId(null);
    try {
      const res = await deepPresetsClient.exportWallets(selectedPresetId, tokens);
      setExportResult(res?.message || (tokens ? `Export started for ${tokens.length.toLocaleString()} token mints` : "Export done"));
      setExportCreated(res);
      setExportTaskId(res.task_id);
      return true;
    } catch (e: any) {
      setExportResult(e?.message || "Export failed");
      return false;
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (!exportTaskId) return;
    let canceled = false;

    const refreshTask = async () => {
      try {
        const task = await deepPresetsClient.getWalletExportTask(exportTaskId);
        if (canceled) return;
        setExportTask(task);

        if (task.exception) {
          setExportResult(task.exception);
          setExportTaskId(null);
        } else if (task.result_sended) {
          setExportResult('Export completed. The file was sent to you by the bot.');
          setExportTaskId(null);
        } else if (task.status === 'done') {
          setExportResult('Export completed. The file will be delivered by the bot.');
          setExportTaskId(null);
        } else if (TERMINAL_WALLET_EXPORT_STATUSES.has(task.status)) {
          setExportResult(`Export ${task.status}.`);
          setExportTaskId(null);
        }
      } catch {
        // Status polling is best-effort; keep the task visible and retry.
      }
    };

    refreshTask();
    const intervalId = window.setInterval(refreshTask, 5000);
    return () => {
      canceled = true;
      window.clearInterval(intervalId);
    };
  }, [exportTaskId]);

  // Handle Excel settings drawer
  const handleOpenExcelSettings = async () => {
    setExcelSettingsLoading(true);
    setExcelDrawerOpened(true);
    try {
      if (selectedPresetId && !isNaN(Number(selectedPresetId))) {
        const settings = await deepPresetsClient.getExcelSettings(Number(selectedPresetId));
        setExcelSettings(settings);
      } else {
        setExcelSettings(null);
      }
    } catch (error) {
      console.error("Failed to load Excel settings:", error);
    } finally {
      setExcelSettingsLoading(false);
    }
  };

  const handleSaveExcelSettings = async (settings: DeepExcelSettings) => {
    try {
      await deepPresetsClient.saveExcelSettings(settings);
      setExcelSettings(settings);
    } catch (error) {
      console.error("Failed to save Excel settings:", error);
      throw error;
    }
  };

  // --- Subscription state ---
  const [subscription, setSubscription] = useState<{
    telegram_id: number;
    subscription_end_at: string;
    subscription_type: string;
  } | null>(null);

  // --- Fetch user subscription on mount ---
  useEffect(() => {
    Telegram.WebApp.expand();
    fetchPresets();
    
    // Generate random positions for icons only once on mount
    const positions = Array(8).fill(0).map((_, i) => ({
      left: `${8 + Math.random() * 76}%`,
      delay: `${i * 0.4 + Math.random() * 0.3}s`,
      size: 15 + Math.random() * 7,
      type: i % 4
    }));
    setIconPositions(positions);
    
    // Fetch winrate histogram once on mount
    deepPresetsClient.getWalletsWinrateStat()
      .then((data: any) => {
        // Convert [{winrate_percent: 0, percent: 39}, ...] to [count0, count1, ..., count100]
        if (Array.isArray(data)) {
          const arr = Array(101).fill(0);
          let total = 0;
          data.forEach(item => {
            if (
              typeof item?.winrate_percent === 'number' &&
              typeof item?.percent === 'number' &&
              item.winrate_percent >= 0 &&
              item.winrate_percent <= 100
            ) {
              arr[item.winrate_percent] = item.percent;
              total += item.percent;
            }
          });
          // Масштабируем к 100% если сумма не 100
          if (total > 0 && total !== 100) {
            for (let i = 0; i < arr.length; i++) {
              arr[i] = (arr[i] / total) * 100;
            }
          }
          setWinrateHistogram(arr);
        } else {
          setWinrateHistogram(null);
        }
      })
      .catch(() => setWinrateHistogram(null));

      deepPresetsClient.getUser()
      .then(data => setSubscription(data))
      .catch(() => setSubscription(null));
  }, []);

  // Add this effect to sync selectedPreset with selectedPresetId
  useEffect(() => {
    if (selectedPresetId) {
      const found = presets.find(p => p.id === selectedPresetId);
      setSelectedPreset(found || null);
    } else {
      setSelectedPreset(null);
    }
  }, [selectedPresetId, presets]);

  // Default values for quick set
  const DEFAULTS = {
    tradeDuration: { min: 60, max: 3600 }, // 1 min - 1 hour
    lastTrade: 24, // 24 hours
    totalTrades: { min: 4, max: 150 },
    winrate: { min: 60, max: 100 },
    pnl: { min: 1, max: 1000 },
    roi: { min: 25, max: 5000 },
    median_mint_roi: { min: 0, max: 1000 },
    avg_mint_roi: { min: 0, max: 1000 },
    usdt: { min: 0, max: 10000 },
    usdc: { min: 0, max: 10000 },
    wsol: { min: 0, max: 1000 },
    sol: { min: 0, max: 1000 },
    total_sol: { min: 0, max: 2000 },
    total_usd: { min: 0, max: 20000 },
    last_trade_time_ago: 24,
    median_mint_traid_duration_seconds: { min: 60, max: 3600 },
    firstSwapTimeAgo: { min: 60*60*24, max: 60*60*24*5 }, // 1 day - 5 days
  };

  const [inputLastTradeMinValue, setInputLastTradeMinValue] = useState<string | undefined>(undefined);
  const [inputLastTradeValue, setInputLastTradeValue] = useState<string | undefined>(undefined);
  const [inputTradeDurationMin, setInputTradeDurationMin] = useState<string | undefined>(undefined);
  const [inputTradeDurationMax, setInputTradeDurationMax] = useState<string | undefined>(undefined);
  const [inputFirstTradeMin, setInputFirstTradeMin] = useState<string | undefined>('');
  const [inputFirstTradeMax, setInputFirstTradeMax] = useState<string | undefined>('');

  // Sync input fields with preset values on preset change
  useEffect(() => {
    setInputLastTradeMinValue(
      selectedPreset?.last_trade_time_ago_min == null
        ? ''
        : formatDuration(selectedPreset.last_trade_time_ago_min)
    );
    setInputLastTradeValue(
      selectedPreset?.last_trade_time_ago == null
        ? ''
        : formatDuration(selectedPreset.last_trade_time_ago)
    );
    setInputTradeDurationMin(
      selectedPreset?.median_mint_traid_duration_seconds_min == null
        ? ''
        : formatDuration(selectedPreset.median_mint_traid_duration_seconds_min)
    );
    setInputTradeDurationMax(
      selectedPreset?.median_mint_traid_duration_seconds_max == null
        ? ''
        : formatDuration(selectedPreset.median_mint_traid_duration_seconds_max)
    );
    setInputFirstTradeMin(
      selectedPreset?.min_first_trade_time_ago == null
        ? ''
        : formatDuration(selectedPreset.min_first_trade_time_ago)
    );
    setInputFirstTradeMax(
      selectedPreset?.max_first_trade_time_ago == null
        ? ''
        : formatDuration(selectedPreset.max_first_trade_time_ago)
    );
  }, [selectedPreset]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        ...modernStyles.container
      }}>
        <Loader size="lg" color={theme.accent.primary} />
      </div>
    );
  }

  // --- Helper: is Premium and active ---
  const isPremiumActive = subscription?.subscription_type === 'Premium'
    && dayjs(subscription.subscription_end_at).isAfter(dayjs());
  const hasSubscription = subscription !== null && dayjs(subscription.subscription_end_at).isAfter(dayjs());
  const exportHasError = Boolean(exportResult && ['ошибка', 'fail', 'error', 'canceled'].some((word) => exportResult.toLowerCase().includes(word)));

  return (
    <MantineProvider
      theme={{
        fontFamily: 'Space Grotesk, sans-serif',
        colors: {
          dark: [
            theme.background.primary,
            '#1C2026', '#23272F', '#23272F', '#23272F',
            '#23272F', '#23272F', '#23272F', '#23272F', '#23272F'
          ],
        },
        primaryColor: 'dark',
        components: {
          Paper: {
            styles: {
              root: {
                background: '#1C2026',
                borderRadius: 16,
                border: '1px solid #23272F',
                boxShadow: '0 4px 32px 0 #00000040',
              },
            },
          },
          Input: {
            styles: {
              input: {
                background: '#181B20',
                border: '1px solid #23272F',
                color: '#F9FAFB',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 400,
                minHeight: 48,
                boxShadow: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                paddingLeft: 18,
                paddingRight: 18,
                '::placeholder': {
                  color: '#6B7280',
                  opacity: 1,
                },
              },
            },
          },
          NumberInput: {
            styles: {
              input: {
                background: '#181B20',
                border: '1px solid #23272F',
                color: '#F9FAFB',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 400,
                minHeight: 48,
                boxShadow: 'none',
                paddingLeft: 18,
                paddingRight: 18,
                '::placeholder': {
                  color: '#6B7280',
                  opacity: 1,
                },
              },
            },
          },
          TextInput: {
            styles: {
              input: {
                background: '#181B20',
                border: '1px solid #23272F',
                color: '#F9FAFB',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 400,
                minHeight: 48,
                boxShadow: 'none',
                paddingLeft: 18,
                paddingRight: 18,
                '::placeholder': {
                  color: '#6B7280',
                  opacity: 1,
                },
              },
            },
          },
          Button: {
            styles: {
              root: {
                borderRadius: 12,
                fontWeight: 500,
                fontSize: 16,
                background: '#181B20',
                color: '#F9FAFB',
                border: '1px solid #23272F',
                boxShadow: 'none',
                transition: 'background 0.2s, color 0.2s',
                '[data-variant="subtle"]': {
                  background: '#181B20',
                  color: '#9CA3AF',
                  border: '1px solid #23272F',
                },
                '[data-variant="subtle"]:hover': {
                  background: '#23272F',
                  color: '#F9FAFB',
                },
                '[data-variant="filled"]': {
                  background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  color: '#F9FAFB',
                  border: 'none',
                },
                '[data-color="green"]': {
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#F9FAFB',
                  border: 'none',
                },
                '[data-color="red"]': {
                  background: '#EF4444',
                  color: '#F9FAFB',
                  border: 'none',
                },
              },
            },
          },
          Notification: {
            styles: {
              root: {
                background: '#181B20',
                border: '1px solid #23272F',
                color: '#F9FAFB',
                borderRadius: 12,
                fontSize: 15,
              },
            },
          },
          Select: {
            styles: {
              input: {
                background: '#181B20',
                border: '1px solid #23272F',
                color: '#F9FAFB',
                borderRadius: 12,
                minHeight: 48,
                fontSize: 16,
                fontWeight: 400,
                paddingLeft: 18,
                paddingRight: 18,
              },
              dropdown: {
                background: '#181B20',
                border: '1px solid #23272F',
                borderRadius: 12,
              },
              item: {
                color: '#F9FAFB',
                '&[data-selected]': {
                  background: '#23272F',
                  color: '#F9FAFB',
                },
              },
            },
          },
          SegmentedControl: {
            styles: {
              root: {
                background: '#181B20',
                border: '1px solid #23272F',
                borderRadius: 12,
              },
              indicator: {
                background: 'rgba(16,185,129,0.13)',
                border: '1.5px solid #10B981',
                borderRadius: 8,
                boxShadow: '0 2px 8px 0 #10B98140',
              },
              label: {
                color: '#F9FAFB',
                fontWeight: 500,
                fontSize: 15,
              },
            },
          },
          Collapse: {
            styles: {
              root: {
                background: '#181B20',
                borderRadius: 16,
                border: '1px solid #23272F',
              },
            },
          },
          Tooltip: {
            styles: {
              tooltip: {
                background: '#23272F',
                color: '#F9FAFB',
                borderRadius: 8,
                fontSize: 14,
              },
            },
          },
          ActionIcon: {
            styles: {
              root: {
                borderRadius: 10,
                background: '#181B20',
                color: '#7C8BA1',
                border: '1px solid #23272F',
                '&:hover': {
                  background: '#23272F',
                  color: '#F9FAFB',
                },
              },
            },
          },
          Group: {
            styles: {
              root: {
                gap: 10,
              },
            },
          },
        },
      }}
    >
      {/* Show save error notification above preset card */}
      {/* Disable horizontal scroll globally */}
      {/* <style>{`body { overflow-x: hidden !important; }`}</style> */}
      <div style={{
        ...modernStyles.container,
        background: theme.background.primary, // changed
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Text
            size="24px"
            fw={700}
            mb={20}
            style={{
              background: `linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.secondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'left'
            }}
          >
            Deep Analysis Presets
          </Text>

          {saveError && (
            <Notification
              color="red"
              withCloseButton
              onClose={() => setSaveError(null)}
              style={{ margin: '16px auto', maxWidth: 400, background: '#3A3D43', border: '1px solid #EF4444', fontWeight: 500 }}
            >
              <span style={{ color: '#EF4444' }}>{"Save error, something went wrong, contact with @scanner527"}</span>
            </Notification>
          )}

          {/* Removed <Paper> here, just render the content directly */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ width: '100%' }}>
              {/* <Text size="sm" c={theme.text.secondary} mb={6}>Current Preset</Text> */}
              <Group gap={8} align="center" style={{ position: 'relative', justifyContent: 'space-between' }}>
                <Select
                  value={selectedPresetId}
                  onChange={setSelectedPresetId}
                  data={presets.map((preset) => ({
                    value: preset.id,
                    label: `${preset.is_active_preset ? '🟢 ' : ''}${preset.name}`,
                  }))}
                  placeholder="Select a preset..."
                  size="sm"
                  styles={{
                    input: {
                      backgroundColor: theme.background.tertiary,
                      border: `1px solid ${theme.border.primary}`,
                      color: theme.text.primary,
                      minHeight: '36px',
                      '&:focus': {
                        borderColor: theme.accent.primary,
                        boxShadow: `0 0 0 2px ${theme.accent.primary}30`
                      }
                    },
                    dropdown: { 
                      backgroundColor: theme.background.elevated,
                      border: `1px solid ${theme.border.primary}`
                    },
                    option: {
                      backgroundColor: theme.background.elevated,
                      color: theme.text.primary,
                      '&[dataSelected]': {
                        backgroundColor: theme.accent.primary
                      },
                      '&:hover': {
                        backgroundColor: theme.background.tertiary
                      }
                    }
                  }}
                />
                <Button
                  leftSection={<IconTrash size={14} />} 
                  onClick={() => selectedPresetId && handleDeletePreset(selectedPresetId)}
                  size="sm"
                  styles={{
                    root: {
                      backgroundColor: theme.state.error,
                      border: 'none',
                      color: theme.text.primary,
                      minHeight: '36px',
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: '#DC2626',
                        transform: 'translateY(-1px)'
                      }
                    }
                  }}
                >
                  Delete
                </Button>
                {/* "New" button moved here, next to Select */}
                <Button
                  leftSection={<IconPlus size={18} />} 
                  onClick={handleCreateNewPreset}
                  size="sm"
                  styles={{
                    root: {
                      background: `linear-gradient(135deg, ${theme.accent.primary} 0%, ${theme.accent.secondary} 100%)`,
                      border: 'none',
                      color: theme.text.primary,
                      fontWeight: 500,
                      minHeight: '36px',
                      minWidth: '136px',
                      fontSize: '14px',
                      '&:hover': {
                        background: `linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)`,
                        transform: 'translateY(-1px)'
                      }
                    }
                  }}
                >
                  New
                </Button>
                {/* Copy button */}
                <Button
                  leftSection={<IconCopy size={16} />} 
                  onClick={handleDuplicatePreset}
                  size="sm"
                  disabled={!selectedPresetId}
                  styles={{
                  root: {
                    background: selectedPresetId ? '#4F46E5' : theme.background.tertiary,
                    border: selectedPresetId ? '1px solid #7C3AED' : `1px solid ${theme.border.primary}`,
                    color: selectedPresetId ? '#fff' : theme.text.disabled,
                    fontWeight: 500,
                    minHeight: '36px',
                    fontSize: '14px',
                    '&:hover': selectedPresetId ? {
                    background: '#7C3AED',
                    border: '1px solid #4F46E5',
                    transform: 'translateY(-1px)'
                    } : {}
                  }
                  }}
                >
                  Copy
                </Button>
                {/* Активная пульсирующая точка вне Select для текущего выбранного */}
                {/* {selectedPreset?.is_active_preset && (
                  <span
                    title="Active preset"
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, #10B981 60%, #059669 100%)',
                      marginLeft: 2,
                      animation: 'pulse-dot 1.2s infinite'
                    }}
                  />
                )} */}
              </Group>
              {/* Export Wallets button moved to main row, where "New" was */}
              <Group gap={8} align="center" style={{ position: 'relative', width: '100%', marginTop: 12 }}>
                {selectedPresetId && (
                  <div style={{ position: 'relative', display: 'inline-block', minWidth: 180 }}>
                    {/* Animated icons */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 2,
                        overflow: 'visible',
                      }}
                    >
                      {/* 3 types of icons: arrow up, candlestick, plus */}
                      {iconPositions.map((pos, i) => {
                        let icon = null;
                        if (pos.type === 0) {
                          // Green trading arrow up
                          icon = (
                            <svg width={pos.size} height={pos.size} viewBox="0 0 20 20" fill="none" style={{ display: 'block' }}>
                              <path d="M10 16V4M10 4L5 9M10 4l5 5" stroke="#01B773" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          );
                        } else if (pos.type === 1) {
                          // Green Japanese candlestick
                          icon = (
                            <svg width={pos.size} height={pos.size} viewBox="0 0 20 20" fill="none" style={{ display: 'block' }}>
                              <rect x="8" y="6" width="4" height="8" rx="1.2" fill="#01B773" />
                              <rect x="9.2" y="3" width="1.6" height="3" rx="0.7" fill="#01B773" />
                              <rect x="9.2" y="14" width="1.6" height="3" rx="0.7" fill="#01B773" />
                            </svg>
                          );
                        } else if (pos.type === 2) {
                          // Green plus
                          icon = (
                            <svg width={pos.size} height={pos.size} viewBox="0 0 20 20" fill="none" style={{ display: 'block' }}>
                              <path d="M10 5v10M5 10h10" stroke="#01B773" strokeWidth="2.2" strokeLinecap="round"/>
                            </svg>
                          );
                        } else if (pos.type === 3) {
                          // Green plus
                            // Green dollar icon
                            icon = (
                            <svg width={pos.size} height={pos.size} viewBox="0 0 20 20" fill="none" style={{ display: 'block' }}>
                              <path d="M10 3v14" stroke="#01B773" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M13.5 6.5c0-1.38-1.57-2.5-3.5-2.5s-3.5 1.12-3.5 2.5c0 1.38 1.57 2.5 3.5 2.5s3.5 1.12 3.5 2.5c0 1.38-1.57 2.5-3.5 2.5s-3.5-1.12-3.5-2.5" stroke="#01B773" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            );
                        }
                        return (
                          <span
                            key={i}
                            style={{
                              position: 'absolute',
                              left: pos.left,
                              marginLeft: `${-pos.size / 2}px`,
                              bottom: '-8px',
                              opacity: 0.7,
                              animation: `floatPlus 2.2s linear ${pos.delay} infinite`,
                              userSelect: 'none',
                              pointerEvents: 'none',
                              filter: 'blur(0.2px)',
                              display: 'block',
                            }}
                          >
                            {icon}
                          </span>
                        );
                      })}
                      <style>
                        {`
                        @keyframes floatPlus {
                          0% {
                            opacity: 0.7;
                            transform: translateY(0) scale(1);
                          }
                          60% {
                            opacity: 0.8;
                            transform: translateY(-18px) scale(1.15);
                          }
                          100% {
                            opacity: 0;
                            transform: translateY(-36px) scale(1.25);
                          }
                        }
                        `}
                      </style>
                    </div>
                    <Button
                      leftSection={<IconDownload size={16} />}
                      size="s"
                      variant="light"
                      data-color="success"
                      loading={exportLoading}
                      // color="green"
                      onClick={() => handleExportWallets()}
                      style={{
                        paddingLeft: 8,
                        fontWeight: 500,
                        background: 'rgba(16, 185, 129, 0.18)',
                        color: '#10A981',
                        border: '1.5px solid #10B981',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        boxShadow: '0 4px 24px 0 rgba(16,185,129,0.18)',
                        transition: 'background 0.2s, border 0.2s',
                        zIndex: 1,
                        position: 'relative',
                      }}
                      onMouseOver={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(16,185,129,0.28)';
                        (e.currentTarget as HTMLElement).style.border = '1.5px solid #059669';
                      }}
                      onMouseOut={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(16,185,129,0.18)';
                        (e.currentTarget as HTMLElement).style.border = '1.5px solid #10B981';
                      }}
                    >
                      Export Wallets🚀
                    </Button>
                  </div>
                )}
                {selectedPresetId && (
                  <TokenWalletExport
                    loading={exportLoading}
                    hasActivityWindow={selectedPreset?.last_trade_time_ago != null || selectedPreset?.last_trade_time_ago_min != null}
                    premiumActive={isPremiumActive}
                    onExport={handleExportWallets}
                  />
                )}
              </Group>
              {/* Export result notification */}
              {exportResult && (
                <Notification
                  color={exportHasError ? "red" : "green"}
                  withCloseButton
                  onClose={() => setExportResult(null)}
                  style={{ marginTop: 8, maxWidth: 400, background: '#3A3D43', border: '1px solid #23272F', }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ color: exportHasError ? "red": "#10B981" }}>{exportResult}</span>
                    {exportCreated && (
                      <small style={{ color: '#9CA3AF' }}>
                        Task #{exportCreated.task_id} · {(exportTask?.status || (exportCreated.queued_for_release ? 'queued for release' : 'created')).replace(/_/g, ' ')}
                      </small>
                    )}
                  </div>
                </Notification>
              )}
            </div>
            {/* Main preset card: remove <Paper>, just use the children */}
            {selectedPreset && (
              <div>
                <Group mb="md" align="center" style={{marginTop: 16}}>
                  <IconEditCircle size={18} color={theme.accent.primary} /> 
                  <TextInput
                    placeholder="Enter preset name..."
                    value={selectedPreset.name}
                    onChange={(e) => setSelectedPreset({ ...selectedPreset, name: e.target.value })}
                    size="sm"
                    styles={{ 
                      input: { 
                        fontSize: '16px',
                        fontWeight: 500,
                        backgroundColor: theme.background.tertiary,
                        border: `1px solid ${theme.border.primary}`,
                        color: theme.text.primary,
                        minHeight: '36px',
                        '&:focus': {
                          borderColor: theme.accent.primary,
                          boxShadow: `0 0 0 2px ${theme.accent.primary}30`
                        }
                      } 
                    }}
                    style={{ flex: 1 }}
                  />
                  {/* Кнопка "Set Active" если не активный, либо метка если активный */}
                  {selectedPreset.is_active_preset ? (
                    <span
                      title="Active preset"
                      style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, #10B981 60%, #059669 100%)',
                        marginLeft: 8,
                        animation: 'pulse-dot 1.2s infinite'
                      }}
                    />
                  ) : (
                    <Button
                      size="xs"
                      color="green"
                      variant="light"
                      onClick={() => handleSetActivePreset(selectedPreset.id)}
                      style={{ marginLeft: 8, fontWeight: 600 }}
                    >
                      Set Active
                    </Button>
                  )}
                </Group>
                {/* Token SOL Invest block */}
                <div style={{ width: '100%', marginBottom: 16 }}>
                  <Group align="center" justify="space-between" gap={6} mb={4}>
                    <Group align="center" gap={6}>
                      <Text fw={500} size="sm" c={theme.text.primary}>
                        Token SOL Invest
                      </Text>
                      <ClickableHelpText text="Select minimum token invested to include wallet in analysis."
                      detailedInfo="Choose token with >= sol invest to calc wallet stat" />
                    </Group>
                    <Button
                      type="button"
                      size="compact-xs"
                      variant="subtle"
                      color="indigo"
                      onClick={() => setTokenSolInvestExampleOpened(true)}
                      styles={{
                        root: { paddingInline: 6, fontWeight: 600 },
                        label: { fontSize: 11 },
                      }}
                    >
                      Show example
                    </Button>
                  </Group>
                    <SegmentedControl
                    fullWidth
                    value={selectedPreset.min_sol_token_invested == null ? 'all' : String(selectedPreset.min_sol_token_invested)}
                    onChange={val => {
                      const newMinSolInvest = val === 'all' ? null : [
                      100000000,
                      50000000,
                      ].includes(Number(val)) ? Number(val) as typeof selectedPreset.min_sol_token_invested : selectedPreset.min_sol_token_invested;
                      
                      // Если выбрали не "all" и текущий period_days был 14 или null (max), то ставим 90
                      let newPeriodDays = selectedPreset.period_days;
                      if (newMinSolInvest !== null && (selectedPreset.period_days === 14 || selectedPreset.period_days === null)) {
                      newPeriodDays = 90;
                      }
                      
                      setSelectedPreset({
                      ...selectedPreset,
                      min_sol_token_invested: newMinSolInvest,
                      period_days: newPeriodDays
                      });
                    }}
                    data={[
                      { label: '0.1', value: '100000000' , disabled: !hasSubscription  },
                      { label: '0.05', value: '50000000' , disabled: !hasSubscription  },
                      { label: 'All', value: 'all'},
                    ]}
                    size="sm"
                    styles={{ root: { marginTop: 4 } }}
                    />
                  {/* Show info if max is disabled */}
                  {!hasSubscription && (
                    <Text size="xs" c={theme.state.warning} mt={6} style={{ textAlign: 'left' }}>
                      Only DAO users can select not All min invest. <span style={{ color: theme.accent.primary }}>Upgrade to DAO</span> to unlock.
                    </Text>
                  )}
                </div>
                {/* Period Days block */}
                <div style={{ width: '100%', marginBottom: 16 }}>
                  <Group align="center" justify="space-between" gap={6} mb={4}>
                    <Group align="center" gap={6}>
                      <Text fw={500} size="sm" c={theme.text.primary}>
                        Period Days
                      </Text>
                      <ClickableHelpText text="Select the period for trade statistics. 'max' means all time."
                      detailedInfo="Choose how many days of trading history to include. Calculated by token last trade day granularity. 'max' means all available data (since 01.01.2025), rocket in max mode are not avalible yet." />
                    </Group>
                    <Button
                      type="button"
                      size="compact-xs"
                      variant="subtle"
                      color="indigo"
                      onClick={() => setPeriodExampleOpened(true)}
                      styles={{
                        root: { paddingInline: 6, fontWeight: 600 },
                        label: { fontSize: 11 },
                      }}
                    >
                      Show example
                    </Button>
                  </Group>
                    <SegmentedControl
                    fullWidth
                    value={selectedPreset.period_days == null ? 'max' : String(selectedPreset.period_days)}
                    onChange={val => setSelectedPreset({
                      ...selectedPreset,
                      period_days: val === 'max' ? null : [1, 7, 14, 30, 45, 90].includes(Number(val)) ? Number(val) as typeof selectedPreset.period_days : selectedPreset.period_days
                    })}
                    data={[
                      { label: '1', value: '1' },
                      { label: '7', value: '7' },
                      ...(selectedPreset.min_sol_token_invested == null ? [{ label: '14', value: '14' }] : []),
                      { label: '30', value: '30' },
                      { label: '45', value: '45' },
                      { label: '90', value: '90' },
                      ...(selectedPreset.min_sol_token_invested == null ? [{ label: 'Max', value: 'max', disabled: !isPremiumActive }] : []),
                    ]}
                    size="sm"
                    styles={{ root: { marginTop: 4 } }}
                    />
                  {/* Show info if max is disabled */}
                  {!isPremiumActive && (
                    <Text size="xs" c={theme.state.warning} mt={6} style={{ textAlign: 'left' }}>
                      Only Premium users can select 'Max' period. <span style={{ color: theme.accent.primary }}>Upgrade to Premium</span> to unlock.
                    </Text>
                  )}
                </div>

                <Grid mb="md" gutter="xs">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <CompactToggle
                      label="Active filters"
                      helpText="Enable or disable all filters"
                      detailedInfo="When enabled, this preset will be used in deep analysis with flowing filtering. Disabled - no filters applied during analysis."
                      checked={selectedPreset.is_active}
                      onChange={(checked) => setSelectedPreset({
                        ...selectedPreset,
                        is_active: checked
                      })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <SegmentedControl
                      value={selectedPreset.format_type}
                      onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                format_type: val as 'EXCEL' | 'TXT_EXCEL' | 'TXT',
                                extended_analytics: val === 'TXT' ? false : selectedPreset.extended_analytics
                              })}
                              // (val as 'EXCEL' | 'TXT_EXCEL' | 'TXT)}
                      data={[
                        { label: 'Excel', value: 'EXCEL' },
                        { label: 'Excel+TXT', value: 'TXT_EXCEL' },
                        { label: 'TXT', value: 'TXT' },
                      ]}
                      fullWidth
                      color={theme.state.success}
                      styles={{
                        root: {
                          background: theme.background.tertiary,
                          border: `1px solid ${theme.border.primary}`,
                          borderRadius: 8,
                          padding: 2,
                        },
                        indicator: {
                          // Glass green with shadow, like theme.state.success15
                          background: 'rgba(16,185,129,0.13)', // theme.state.success15
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          borderRadius: 6,
                          boxShadow: `0 2px 8px 0 ${theme.state.success}40`,
                          border: `1.5px solid ${theme.state.success}`,
                        },
                        label: {
                          color: theme.text.primary,
                          fontWeight: 500,
                          fontSize: 14,
                          padding: '6px 0',
                        },
                        control: {
                          borderRadius: 6,
                        },
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    { selectedPreset.format_type !== 'TXT' ?
                    <CompactToggle
                      label="Extended mode"
                      helpText="Includes tokens in Excel"
                      detailedInfo="Enables token lists in Excel. May increase processing time. Also limiting wallets amount per one export" 
                      checked={selectedPreset.extended_analytics}
                      // disabled={selectedPreset.format_type === 'TXT'}
                      onChange={(checked) => setSelectedPreset({
                        ...selectedPreset,
                        extended_analytics: checked
                      })}
                      />
                    :  <Text fw={500} size="sm" c={theme.text.primary} style={{align: 'left'}}>Extended analytics are not available for TXT format</Text>}
                  </Grid.Col>
                </Grid>

                {/* Excel settings are available even when filters are inactive. */}
                {selectedPreset.format_type !== 'TXT' && (
                  <Button
                    onClick={handleOpenExcelSettings}
                    leftSection={<IconSettings size={16} />}
                    size="md"
                    variant="light"
                    loading={excelSettingsLoading}
                    style={{
                      margin: '0 0 16px 0',
                      borderRadius: 12,
                      fontWeight: 600,
                      fontSize: 15,
                      background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                      color: '#fff',
                      border: 'none',
                      boxShadow: '0 4px 16px 0 rgba(79, 70, 229, 0.3)',
                      transition: 'all 0.2s ease',
                      padding: '12px 24px',
                      letterSpacing: 0.3,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px 0 rgba(79, 70, 229, 0.4)',
                      }
                    }}
                  >
                    📊 Excel Style Settings
                  </Button>
                )}

                {/* Скрываем фильтры если is_active выключен */}
                {selectedPreset.is_active && (
                  <>
                    {/* Performance Metrics Section */}
                    <div style={modernStyles.collapseHeader} onClick={() => setPerformanceExpanded(!performanceExpanded)}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconTrendingUp size={14} color={theme.accent.primary} /> 
                          <Text fw={500} c={theme.text.primary} size="sm">Performance</Text>
                        </Group>
                        {performanceExpanded ? <IconChevronUp size={14} color={theme.text.secondary} /> : <IconChevronDown size={14} color={theme.text.secondary} />}
                      </Group>
                    </div>
                    <Collapse in={performanceExpanded}>
                      <div style={{ padding: '12px', background: theme.filterGradient, borderRadius: '8px', border: `1px solid ${theme.border.primary}`, marginBottom: '5px' }}>
                        {/* --- WinrateHistogramSlider вместо MinMaxInputPair --- */}
                        <WinrateHistogramSlider
                          min={0}
                          max={100}
                          value={[
                            selectedPreset.min_winrate == null ? 0 : selectedPreset.min_winrate,
                            selectedPreset.max_winrate == null ? 100 : selectedPreset.max_winrate
                          ]}
                          onChange={([min, max]) => setSelectedPreset({
                            ...selectedPreset,
                            min_winrate: min,
                            max_winrate: max,
                          })}
                          histogram={winrateHistogram || undefined}
                          // sliderFill={theme.sliderFill} // pass fill color
                          data-color="green" // use data-color for custom color
                        />
                        {/* --- конец замены --- */}
                        <MinMaxInputPair
                          label="PNL (SOL)"
                          helpText="Profit and loss range"
                          detailedInfo="Total profit or loss across all trades. Positive values indicate overall profit, negative values indicate losses. Measured in SOL or USD equivalent."
                          minValue={selectedPreset.pnl_min}
                          maxValue={selectedPreset.pnl_max}
                          onMinChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            pnl_min: value
                          })}
                          onMaxChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            pnl_max: value
                          })}
                          groupButtons={[{
                            icon: <IconSettings size={14} />, label: 'Default (0-10000)', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              pnl_min: DEFAULTS.pnl.min,
                              pnl_max: DEFAULTS.pnl.max,
                            })
                          }, {
                            icon: <IconBan size={14} />, label: 'Disable (null)', color: 'red', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              pnl_min: null as any,
                              pnl_max: null as any,
                            })
                          }]}
                        />
                      </div>
                    </Collapse>

                    {/* ROI Section */}
                    <div style={{...modernStyles.collapseHeader, borderLeft: `3px solid ${theme.accent.secondary}`}} onClick={() => setRoiExpanded(!roiExpanded)}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconChartBar size={14} color={theme.accent.secondary} />
                          <Text fw={500} c={theme.text.primary} size="sm">ROI Settings</Text>
                        </Group>
                        {roiExpanded ? <IconChevronUp size={14} color={theme.text.secondary} /> : <IconChevronDown size={14} color={theme.text.secondary} />}
                      </Group>
                    </div>
                    <Collapse in={roiExpanded}>
                      <div style={{ padding: '12px', background: theme.filterGradient, borderRadius: '8px', border: `1px solid ${theme.border.primary}`, marginBottom: '5px' }}>
                        <MinMaxInputPair
                          label="ROI %"
                          helpText="Return on investment range"
                          detailedInfo="Overall return on investment percentage. Calculated as (final value - initial investment) / initial investment * 100. Higher ROI indicates better investment performance."
                          minValue={selectedPreset.min_roi}
                          maxValue={selectedPreset.max_roi}
                          onMinChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            min_roi: value
                          })}
                          onMaxChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            max_roi: value
                          })}
                          groupButtons={[{
                            icon: <IconSettings size={14} />, label: 'Default (0-10000)', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_roi: DEFAULTS.roi.min,
                              max_roi: DEFAULTS.roi.max,
                            })
                          }, {
                            icon: <IconBan size={14} />, label: 'Disable (null)', color: 'red', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_roi: null as any,
                              max_roi: null as any,
                            })
                          }]}
                        />
                        <MinMaxInputPair
                          label="Median ROI"
                          helpText="Median mint ROI range"
                          detailedInfo="The middle value of all mint ROIs when sorted from lowest to highest. Less affected by extreme outliers than average, providing a more stable measure of typical performance."
                          minValue={selectedPreset.min_median_mint_roi}
                          maxValue={selectedPreset.max_median_mint_roi}
                          onMinChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            min_median_mint_roi: value
                          })}
                          onMaxChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            max_median_mint_roi: value
                          })}
                          groupButtons={[{
                            icon: <IconSettings size={14} />, label: 'Default (0-10000)', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_median_mint_roi: DEFAULTS.median_mint_roi.min,
                              max_median_mint_roi: DEFAULTS.median_mint_roi.max,
                            })
                          }, {
                            icon: <IconBan size={14} />, label: 'Disable (null)', color: 'red', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_median_mint_roi: null as any,
                              max_median_mint_roi: null as any,
                            })
                          }]}
                        />
                        <MinMaxInputPair
                          label="Avg ROI"
                          helpText="Average mint ROI range"
                          detailedInfo="Average return on investment across all mint trades. Can be skewed by extremely profitable or losing trades, but shows overall performance trend."
                          minValue={selectedPreset.min_avg_mint_roi}
                          maxValue={selectedPreset.max_avg_mint_roi}
                          onMinChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            min_avg_mint_roi: value
                          })}
                          onMaxChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            max_avg_mint_roi: value
                          })}
                          groupButtons={[{
                            icon: <IconSettings size={14} />, label: 'Default (0-10000)', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_avg_mint_roi: DEFAULTS.avg_mint_roi.min,
                              max_avg_mint_roi: DEFAULTS.avg_mint_roi.max,
                            })
                          }, {
                            icon: <IconBan size={14} />, label: 'Disable (null)', color: 'red', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              min_avg_mint_roi: null as any,
                              max_avg_mint_roi: null as any,
                            })
                          }]}
                        />
                      </div>
                    </Collapse>

                    {/* Balances Section */}
                    <div style={{...modernStyles.collapseHeader, borderLeft: `3px solid ${theme.accent.tertiary}`}} onClick={() => setBalancesExpanded(!balancesExpanded)}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconWallet size={14} color={theme.accent.tertiary} />
                          <Text fw={500} c={theme.text.primary} size="sm">Balances</Text>
                        </Group>
                        {balancesExpanded ? <IconChevronUp size={14} color={theme.text.secondary} /> : <IconChevronDown size={14} color={theme.text.secondary} />}
                      </Group>
                    </div>
                    <Collapse in={balancesExpanded}>
                      <div style={{ padding: '12px', background: theme.filterGradient, borderRadius: '8px', border: `1px solid ${theme.border.primary}`, marginBottom: '5px' }}>
                        {/* SOL Section */}
                        <div style={{ marginBottom: 16 }}>
                          <Group justify="space-between" mb={8}>
                            <Text fw={500} size="sm" c={theme.text.primary}>
                              Total SOL
                            </Text>
                            <Group style={{ gap: 4 }}>
                              <ActionIcon 
                                size="xs" 
                                variant="subtle" 
                                color="blue" 
                                onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_min: DEFAULTS.total_sol.min,
                                    total_sol_max: DEFAULTS.total_sol.max,
                                  });
                                }}
                                title="Default values"
                              >
                                <IconSettings size={14} />
                              </ActionIcon>
                              <ActionIcon 
                                size="xs" 
                                variant="subtle" 
                                color="red" 
                                onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_min: null as any,
                                    total_sol_max: null as any,
                                  });
                                }}
                                title="Clear values"
                              >
                                <IconBan size={14} />
                              </ActionIcon>
                            </Group>
                          </Group>
                          
                          <Group grow mb={8}>
                            <NumberInput
                              placeholder="Min"
                              value={selectedPreset.total_sol_min ?? ''}
                              onChange={(val) => {
                                if (val === '' || val === null) {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_min: null as any
                                  });
                                } else {
                                  const num = typeof val === 'number' ? val : parseFloat(val);
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_min: isNaN(num) ? null as any : num
                                  });
                                }
                              }}
                              min={0}
                              size="sm"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '32px',
                                  fontSize: '14px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="Max"
                              value={selectedPreset.total_sol_max ?? ''}
                              onChange={(val) => {
                                if (val === '' || val === null) {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_max: null as any
                                  });
                                } else {
                                  const num = typeof val === 'number' ? val : parseFloat(val);
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_sol_max: isNaN(num) ? null as any : num
                                  });
                                }
                              }}
                              min={selectedPreset.total_sol_min ?? 0}
                              size="sm"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '32px',
                                  fontSize: '14px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                          
                          <Button
                            fullWidth
                            variant="subtle"
                            color="gray"
                            rightSection={solDetailedOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                            onClick={() => setSolDetailedOpen(v => !v)}
                            styles={{
                              root: {
                                fontSize: '13px',
                                padding: '2px 8px',
                                height: 'auto',
                                color: theme.text.secondary,
                                '&:hover': {
                                  backgroundColor: theme.background.tertiary
                                }
                              }
                            }}
                          >
                            Show details
                          </Button>
                          
                          <Collapse in={solDetailedOpen}>
                            <div style={{ marginTop: 10 }}>
                              {/* SOL Input */}
                              <Group justify="space-between" mb={4}>
                                <Text size="xs" fw={500} c={theme.text.secondary}>SOL</Text>
                              </Group>
                              <Group grow mb={10}>
                                <NumberInput
                                  placeholder="Min"
                                  value={selectedPreset.sol_min ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        sol_min: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        sol_min: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                                <NumberInput
                                  placeholder="Max"
                                  value={selectedPreset.sol_max ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        sol_max: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        sol_max: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={selectedPreset.sol_min ?? 0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                              </Group>
                              
                              {/* WSOL Input */}
                              <Group justify="space-between" mb={4}>
                                <Text size="xs" fw={500} c={theme.text.secondary}>WSOL</Text>
                              </Group>
                              <Group grow mb={4}>
                                <NumberInput
                                  placeholder="Min"
                                  value={selectedPreset.wsol_min ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        wsol_min: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        wsol_min: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                                <NumberInput
                                  placeholder="Max"
                                  value={selectedPreset.wsol_max ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        wsol_max: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        wsol_max: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={selectedPreset.wsol_min ?? 0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                              </Group>
                            </div>
                          </Collapse>
                        </div>

                        {/* USD Section */}
                        <div>
                          <Group justify="space-between" mb={8}>
                            <Text fw={500} size="sm" c={theme.text.primary}>
                              Total USD
                            </Text>
                            <Group style={{ gap: 4 }}>
                              <ActionIcon 
                                size="xs" 
                                variant="subtle" 
                                color="blue" 
                                onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_min: DEFAULTS.total_usd.min,
                                    total_usd_max: DEFAULTS.total_usd.max,
                                  });
                                }}
                                title="Default values"
                              >
                                <IconSettings size={14} />
                              </ActionIcon>
                              <ActionIcon 
                                size="xs" 
                                variant="subtle" 
                                color="red" 
                                onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_min: null as any,
                                    total_usd_max: null as any,
                                  });
                                }}
                                title="Clear values"
                              >
                                <IconBan size={14} />
                              </ActionIcon>
                            </Group>
                          </Group>
                          
                          <Group grow mb={8}>
                            <NumberInput
                              placeholder="Min"
                              value={selectedPreset.total_usd_min ?? ''}
                              onChange={(val) => {
                                if (val === '' || val === null) {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_min: null as any
                                  });
                                } else {
                                  const num = typeof val === 'number' ? val : parseFloat(val);
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_min: isNaN(num) ? null as any : num
                                  });
                                }
                              }}
                              min={0}
                              size="sm"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '32px',
                                  fontSize: '14px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="Max"
                              value={selectedPreset.total_usd_max ?? ''}
                              onChange={(val) => {
                                if (val === '' || val === null) {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_max: null as any
                                  });
                                } else {
                                  const num = typeof val === 'number' ? val : parseFloat(val);
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    total_usd_max: isNaN(num) ? null as any : num
                                  });
                                }
                              }}
                              min={selectedPreset.total_usd_min ?? 0}
                              size="sm"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '32px',
                                  fontSize: '14px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                          
                          <Button
                            fullWidth
                            variant="subtle"
                            color="gray"
                            
                            rightSection={usdDetailedOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                            onClick={() => setUsdDetailedOpen(v => !v)}
                            styles={{
                              root: {
                                fontSize: '13px',
                                padding: '2px 8px',
                                height: 'auto',
                                color: theme.text.secondary,
                                '&:hover': {
                                  backgroundColor: theme.background.tertiary
                                }
                              }
                            }}
                          >
                            Show details
                          </Button>
                          
                          <Collapse in={usdDetailedOpen}>
                            <div style={{ marginTop: 10 }}>
                              {/* USDT Input */}
                              <Group justify="space-between" mb={4}>
                                <Text size="xs" fw={500} c={theme.text.secondary}>USDT</Text>
                              </Group>
                              <Group grow mb={10}>
                                <NumberInput
                                  placeholder="Min"
                                  value={selectedPreset.usdt_min ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdt_min: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdt_min: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                                <NumberInput
                                  placeholder="Max"
                                  value={selectedPreset.usdt_max ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdt_max: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdt_max: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={selectedPreset.usdt_min ?? 0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                              </Group>
                              
                              {/* USDC Input */}
                              <Group justify="space-between" mb={4}>
                                <Text size="xs" fw={500} c={theme.text.secondary}>USDC</Text>
                              </Group>
                              <Group grow mb={4}>
                                <NumberInput
                                  placeholder="Min"
                                  value={selectedPreset.usdc_min ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdc_min: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdc_min: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                                <NumberInput
                                  placeholder="Max"
                                  value={selectedPreset.usdc_max ?? ''}
                                  onChange={(val) => {
                                    if (val === '' || val === null) {
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdc_max: null as any
                                      });
                                    } else {
                                      const num = typeof val === 'number' ? val : parseFloat(val);
                                      setSelectedPreset({
                                        ...selectedPreset,
                                        usdc_max: isNaN(num) ? null as any : num
                                      });
                                    }
                                  }}
                                  min={selectedPreset.usdc_min ?? 0}
                                  size="xs"
                                  styles={{
                                    input: {
                                      backgroundColor: theme.background.secondary,
                                      border: `1px solid ${theme.border.primary}`,
                                      color: theme.text.primary,
                                    }
                                  }}
                                  hideControls
                                />
                              </Group>
                            </div>
                          </Collapse>
                        </div>
                      </div>
                    </Collapse>

                    {/* Trading Activity Section */}
                    <div style={{...modernStyles.collapseHeader, borderLeft: `3px solid ${theme.accent.secondary}`}} onClick={() => setTradingExpanded(!tradingExpanded)}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconClock size={14} color={theme.accent.secondary} />
                          <Text fw={500} c={theme.text.primary} size="sm">Trading</Text>
                        </Group>
                        {tradingExpanded ? <IconChevronUp size={14} color={theme.text.secondary} /> : <IconChevronDown size={14} color={theme.text.secondary} />}
                      </Group>
                    </div>
                    <Collapse in={tradingExpanded}>
                      <div style={{ padding: '12px', background: theme.filterGradient, borderRadius: '8px', border: `1px solid ${theme.border.primary}`, marginBottom: '5px' }}>
                        {/* Trade Providers Filter */}
                        <TradeProvidersFilter
                          value={selectedPreset.trade_providers || {}}
                          onChange={tp => setSelectedPreset({
                            ...selectedPreset,
                            trade_providers: tp
                          })}
                        />
                        {/* Pool Providers Filter */}
                        <PoolProvidersFilter
                          value={selectedPreset.pool_providers || {}}
                          onChange={pp => setSelectedPreset({
                            ...selectedPreset,
                            pool_providers: pp
                          })}
                        />
                        {/* --- PoolTypeProvidersFilter: новый фильтр --- */}
                        <PoolTypeProvidersFilter
                          value={selectedPreset.pool_type_providers}
                          onChange={ptp => setSelectedPreset({
                            ...selectedPreset,
                            pool_type_providers: ptp && Object.keys(ptp.values || {}).length > 0 ? ptp : undefined
                          })}
                        />
                        {/* Aggregators Map filter */}
                        <AggregatorsMapFilter
                          value={selectedPreset.aggregators_map}
                          onChange={am => setSelectedPreset({
                            ...selectedPreset,
                            aggregators_map: am && Object.keys(am.values || {}).length > 0 ? am : undefined
                          })}
                        />
                        {/* Last Trade (min/max) filter */}
                        <div style={{ marginBottom: '12px' }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Group align="center" gap={4}>
                              <Text size="sm" fw={500}>Last Trade</Text>
                              <ClickableHelpText
                                text="Allowed age: Min ≤ time since last trade < Max."
                                detailedInfo="For a bounded interval, Last Trade Min must be smaller than Last Trade Max. Format: 1d2h30m10s."
                              />
                              <Button
                                type="button"
                                size="compact-xs"
                                variant="subtle"
                                color="indigo"
                                onClick={() => setTradeTimeExampleMode('last')}
                                styles={{ root: { paddingInline: 5 }, label: { fontSize: 10, fontWeight: 600 } }}
                              >
                                Show example
                              </Button>
                            </Group>
                            <Group align="center" gap={2}>
                              <Tooltip label="Default" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" onClick={() => {
                                  setInputLastTradeMinValue('');
                                  setInputLastTradeValue('24h');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    last_trade_time_ago_min: undefined,
                                    last_trade_time_ago: 24 * 3600
                                  });
                                }}>
                                  <IconSettings size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Disable (null)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" color="red" onClick={() => {
                                  setInputLastTradeMinValue('');
                                  setInputLastTradeValue('');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    last_trade_time_ago_min: undefined,
                                    last_trade_time_ago: undefined
                                  });
                                }}>
                                  <IconBan size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                          <Grid gutter="xs">
                            <Grid.Col span={6}>
                              <TextInput
                                value={inputLastTradeMinValue !== undefined
                                  ? inputLastTradeMinValue
                                  : (selectedPreset.last_trade_time_ago_min == null ? '' : formatDuration(selectedPreset.last_trade_time_ago_min))}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInputLastTradeMinValue(val);
                                }}
                                onBlur={e => {
                                  const val = e.target.value;
                                  const parsed = parseDurationStr(val);
                                  setInputLastTradeMinValue(parsed ? formatDuration(parsed) : '');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    last_trade_time_ago_min: parsed
                                  });
                                }}
                                size="sm"
                                placeholder="Min (e.g. 1h)"
                                styles={{
                                  input: {
                                    backgroundColor: theme.background.secondary,
                                    border: `1px solid ${theme.border.primary}`,
                                    color: theme.text.primary,
                                    minHeight: '32px',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                  }
                                }}
                              />
                            </Grid.Col>
                            <Grid.Col span={6}>
                              <TextInput
                                value={inputLastTradeValue !== undefined
                                  ? inputLastTradeValue
                                  : (selectedPreset.last_trade_time_ago == null ? '' : formatDuration(selectedPreset.last_trade_time_ago))}
                                onChange={e => {
                                  const val = e.target.value;
                                  setInputLastTradeValue(val);
                                }}
                                onBlur={e => {
                                  const val = e.target.value;
                                  const parsed = parseDurationStr(val);
                                  setInputLastTradeValue(parsed ? formatDuration(parsed) : '');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    last_trade_time_ago: parsed
                                  });
                                }}
                                size="sm"
                                placeholder="Max (e.g. 24h)"
                                styles={{
                                  input: {
                                    backgroundColor: theme.background.secondary,
                                    border: `1px solid ${theme.border.primary}`,
                                    color: theme.text.primary,
                                    minHeight: '32px',
                                    fontSize: '14px',
                                    textAlign: 'center',
                                  }
                                }}
                              />
                            </Grid.Col>
                          </Grid>
                        </div>
                        {/* First Trade (min/max) filter */}
                        <div style={{ marginBottom: '12px' }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Group align="center" gap={4}>
                              <Text size="sm" fw={500}>First Trade</Text>
                              <ClickableHelpText
                                text="Allowed age: Max ≤ time since first trade ≤ Min."
                                detailedInfo="For a bounded interval, First Trade Min must be greater than or equal to First Trade Max. Format: 1d2h30m10s."
                              />
                              <Button
                                type="button"
                                size="compact-xs"
                                variant="subtle"
                                color="indigo"
                                onClick={() => setTradeTimeExampleMode('first')}
                                styles={{ root: { paddingInline: 5 }, label: { fontSize: 10, fontWeight: 600 } }}
                              >
                                Show example
                              </Button>
                            </Group>
                            <Group align="center" gap={2}>
                              <Tooltip label="Default (no min, 1y max)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" onClick={() => {
                                  setInputFirstTradeMin('');
                                  setInputFirstTradeMax('365d');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    min_first_trade_time_ago: undefined,
                                    max_first_trade_time_ago: 365 * 24 * 3600
                                  });
                                }}>
                                  <IconSettings size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Disable (null)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" color="red" onClick={() => {
                                  setInputFirstTradeMin('');
                                  setInputFirstTradeMax('');
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    min_first_trade_time_ago: undefined,
                                    max_first_trade_time_ago: undefined
                                  });
                                }}>
                                  <IconBan size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                          <Group gap={6} align="center" style={{ flexWrap: 'nowrap' }}>
                            <TextInput
                              value={inputFirstTradeMin !== undefined
                                ? inputFirstTradeMin
                                : (selectedPreset.min_first_trade_time_ago == null ? '' : formatDuration(selectedPreset.min_first_trade_time_ago))}
                              onChange={e => {
                                const val = e.target.value;
                                setInputFirstTradeMin(val);
                              }}
                              onBlur={e => {
                                const val = e.target.value;
                                const parsed = parseDurationStr(val);
                                setInputFirstTradeMin(parsed ? formatDuration(parsed) : '');
                                setSelectedPreset({
                                  ...selectedPreset,
                                  min_first_trade_time_ago: parsed
                                });
                              }}
                              size="xs"
                              placeholder="Min"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '13px',
                                  textAlign: 'center',
                                  width: 130
                                }
                              }}
                            />
                            <TextInput
                              value={inputFirstTradeMax !== undefined
                                ? inputFirstTradeMax
                                : (selectedPreset.max_first_trade_time_ago == null ? '' : formatDuration(selectedPreset.max_first_trade_time_ago))}
                              onChange={e => {
                                const val = e.target.value;
                                setInputFirstTradeMax(val);
                              }}
                              onBlur={e => {
                                const val = e.target.value;
                                const parsed = parseDurationStr(val);
                                setInputFirstTradeMax(parsed ? formatDuration(parsed) : '');
                                setSelectedPreset({
                                  ...selectedPreset,
                                  max_first_trade_time_ago: parsed
                                });
                              }}
                              size="xs"
                              placeholder="Max"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '13px',
                                  textAlign: 'center',
                                  width: 130
                                }
                              }}
                            />
                          </Group>
                        </div>
                        {/* Total Trades with group micro buttons */}
                        <MinMaxInputPair
                          label="Total Trades"
                          helpText="Total traded mints range"
                          minValue={selectedPreset.total_tarded_mints_min}
                          maxValue={selectedPreset.total_tarded_mints_max}
                          onMinChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            total_tarded_mints_min: value
                          })}
                          onMaxChange={(value) => setSelectedPreset({
                            ...selectedPreset,
                            total_tarded_mints_max: value
                          })}
                          min={0}
                          max={100000}
                          // group micro buttons
                          groupButtons={[{
                            icon: <IconSettings size={14} />, label: 'Default (10-1000)', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              total_tarded_mints_min: DEFAULTS.totalTrades.min,
                              total_tarded_mints_max: DEFAULTS.totalTrades.max,
                            })
                          }, {
                            icon: <IconBan size={14} />, label: 'Disable (null)', color: 'red', onClick: () => setSelectedPreset({
                              ...selectedPreset,
                              total_tarded_mints_min: null as any,
                              total_tarded_mints_max: null as any,
                            })
                          }]}
                        />
                        {/* Rockets count block - only show if period_days !== null */}
                        {selectedPreset.period_days !== null && (
                          <div style={{ marginTop: 12 }}>
                            <Text fw={500} size="sm" c={theme.text.primary} mb={4}>
                              <span style={{ fontSize: 18, marginRight: 6 }}>🚀</span>Rockets count
                            </Text>
                                                       <Group gap={12}>
                              <div style={{ flex: 1 }}>
                                <NumberInput
                                  value={selectedPreset.min_rocket_x2_count ?? ''}
                                  onChange={val => setSelectedPreset({
                                    ...selectedPreset,
                                    min_rocket_x2_count: val === '' || val === null ? null : Number(val)
                                  })}
                                  min={0}
                                  size="s"
                                  styles={{ input: { backgroundColor: theme.background.secondary, border: `1px solid ${theme.border.primary}`, color: theme.text.primary, minHeight: '32px', fontSize: '14px', textAlign: 'center' } }}
                                  hideControls
                                  placeholder="x2"
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <NumberInput
                                  value={selectedPreset.min_rocket_x5_count ?? ''}
                                  onChange={val => setSelectedPreset({
                                    ...selectedPreset,
                                    min_rocket_x5_count: val === '' || val === null ? null : Number(val)
                                  })}
                                  min={0}
                                  size="s"
                                  styles={{ input: { backgroundColor: theme.background.secondary, border: `1px solid ${theme.border.primary}`, color: theme.text.primary, minHeight: '32px', fontSize: '14px', textAlign: 'center' } }}
                                  hideControls
                                  placeholder="x5"
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <NumberInput
                                  value={selectedPreset.min_rocket_x10_count ?? ''}
                                  onChange={val => setSelectedPreset({
                                    ...selectedPreset,
                                    min_rocket_x10_count: val === '' || val === null ? null : Number(val)
                                  })}
                                  min={0}
                                  size="s"
                                  styles={{ input: { backgroundColor: theme.background.secondary, border: `1px solid ${theme.border.primary}`, color: theme.text.primary, minHeight: '32px', fontSize: '14px', textAlign: 'center' } }}
                                  hideControls
                                  placeholder="x10"
                                />
                              </div>
                            </Group>
                            <Text size="xs" c={theme.text.secondary} mt={4}>
                              Tokens count with x2, x5, x10 roi
                            </Text>
                          </div>
                        )}
                        {/* --- конец блока Rockets count --- */}
                        {/* Trade Duration with group micro buttons */}
                        <div style={{ marginTop: 12 }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Group align="center" gap={4}>
                              <Text size="sm" fw={500}>Trade Dur</Text>
                              <ClickableHelpText text="Trade duration range" detailedInfo="Set the min/max duration for trades in seconds. Format: 1d2h30m45s. Median" />
                            </Group>
                            <Group align="center" gap={2}>
                              <Tooltip label="Default (1m-1h)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    median_mint_traid_duration_seconds_min: DEFAULTS.tradeDuration.min,
                                    median_mint_traid_duration_seconds_max: DEFAULTS.tradeDuration.max,
                                  });
                                }}>
                                  <IconSettings size={14} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Disable (null)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" color="red" onClick={() => {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    median_mint_traid_duration_seconds_min: undefined,
                                    median_mint_traid_duration_seconds_max: undefined,
                                  });
                                }}>
                                  <IconBan size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                          {/* Only show the DurationRangeSlider, remove manual input fields */}
                          <DurationRangeSlider
                            minValue={selectedPreset.median_mint_traid_duration_seconds_min}
                            maxValue={selectedPreset.median_mint_traid_duration_seconds_max}
                            onChange={(min, max) => {
                              setSelectedPreset({
                                ...selectedPreset,
                                median_mint_traid_duration_seconds_min: min,
                                median_mint_traid_duration_seconds_max: max
                              });
                              setInputTradeDurationMin(min == null ? '' : formatDuration(min));
                              setInputTradeDurationMax(max == null ? '' : formatDuration(max));
                            }}
                            inputStyles={{
                              input: {
                                backgroundColor: theme.background.secondary,
                                border: `1px solid ${theme.border.primary}`,
                                color: theme.text.primary,
                                minHeight: '32px',
                                fontSize: '14px',
                                textAlign: 'center',
                              }
                            }}
                          />
                        </div>
                        {/* --- NEW: sold_more_than_bought_trades_max and sold_before_buy_mints_percent_max --- */}
                        <Group grow gap={8} mt={12} mb={8} align="flex-end" style={{ flexWrap: 'wrap' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Group mb={4} align="center" gap={4} justify="space-between" style={{ flexWrap: 'nowrap' }}>
                              <Text size="sm" fw={500}>Sold &gt; Bought</Text>
                              {/* <Tooltip label="Max % of trades where sold amount &gt; bought" position="top" withArrow>
                                <ClickableHelpText
                                  text="Max % of trades where sold amount > bought"
                                  detailedInfo="Limits the maximum percent of trades where the sold amount is greater than the bought amount. 0 disables filter."
                                />
                              </Tooltip> */}
                              <Tooltip label="Disable (null)" position="left" withArrow>
                                <ActionIcon size="xs" variant="subtle" color="red" onClick={() => setSelectedPreset({
                                  ...selectedPreset,
                                  sold_more_than_bought_trades_max: null
                                })}>
                                  <IconBan size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                            <NumberInput
                              value={selectedPreset.sold_more_than_bought_trades_max ?? ''}
                              onChange={val => {
                                if (val === '' || val === null) {
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    sold_more_than_bought_trades_max: null
                                  });
                                } else {
                                  const num = typeof val === 'number' ? val : parseFloat(val);
                                  setSelectedPreset({
                                    ...selectedPreset,
                                    sold_more_than_bought_trades_max: isNaN(num) ? null : Math.max(0, Math.min(100, num))
                                  });
                                }
                              }}
                              min={0}
                              max={100}
                              step={1}
                              size="sm"
                              rightSection={<span style={{ color: theme.text.secondary, fontSize: 13, marginRight: 6 }}>%</span>}
                              inputMode="decimal"
                              pattern="[0-9]*[.,]?[0-9]*"
                              styles={{
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '32px',
                                  fontSize: '14px',
                                  textAlign: 'center',
                                  width: 120
                                }
                              }}
                              hideControls
                              placeholder="max %"
                            />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Group mb={4} align="center" gap={4} justify="space-between" style={{ flexWrap: 'nowrap' }}>
                              <Text size="sm" fw={500}>Sold before Buy</Text>
                              {/* <Tooltip label="Max % of mints sold before buy" position="top" withArrow> */}
                                {/* <ClickableHelpText
                                  text="Max % of mints sold before buy"
                                  detailedInfo="Limits the maximum percent of mints where the sold event happened before buy. 0 disables filter."
                                /> */}
                            {/* </Tooltip> */}
                            <Tooltip label="Disable (null)" position="left" withArrow>
                              <ActionIcon size="xs" variant="subtle" color="red" onClick={() => setSelectedPreset({
                                ...selectedPreset,
                                sold_before_buy_mints_percent_max: null
                              })}>
                                <IconBan size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          <NumberInput
                            value={selectedPreset.sold_before_buy_mints_percent_max ?? ''}
                            onChange={val => {
                              if (val === '' || val === null) {
                                setSelectedPreset({
                                  ...selectedPreset,
                                  sold_before_buy_mints_percent_max: null
                                });
                              } else {
                                const num = typeof val === 'number' ? val : parseFloat(val);
                                setSelectedPreset({
                                  ...selectedPreset,
                                  sold_before_buy_mints_percent_max: isNaN(num) ? null : Math.max(0, Math.min(100, num))
                                });
                              }
                            }}
                            min={0}
                            max={100}
                            step={1}
                            size="sm"
                            rightSection={<span style={{ color: theme.text.secondary, fontSize: 13, marginRight: 6 }}>%</span>}
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]*"
                            styles={{
                              input: {
                                backgroundColor: theme.background.secondary,
                                border: `1px solid ${theme.border.primary}`,
                                color: theme.text.primary,
                                minHeight: '32px',
                                fontSize: '14px',
                                textAlign: 'center',
                                width: 120
                              }
                            }}
                            hideControls
                            placeholder="max %"
                          />
                        </div>
                      </Group>
                      {/* --- END NEW --- */}
                    </div>
                  </Collapse>

                    {/* MCAP Section */}
                    <div style={{...modernStyles.collapseHeader, borderLeft: `3px solid #F59E0B`}} onClick={() => setMcapExpanded(!mcapExpanded)}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconChartBar size={14} color="#F59E0B" />
                          <Text fw={500} c={theme.text.primary} size="sm">MCAP Filters</Text>
                        </Group>
                        {mcapExpanded ? <IconChevronUp size={14} color={theme.text.secondary} /> : <IconChevronDown size={14} color={theme.text.secondary} />}
                      </Group>
                    </div>
                    <Collapse in={mcapExpanded}>
                      <div style={{ padding: '12px', background: theme.filterGradient, borderRadius: '8px', border: `1px solid ${theme.border.primary}`, marginBottom: '5px' }}>
                        
                        {/* Average MCAP */}
                        <Text fw={600} size="sm" c={theme.text.primary} mb={8} style={{ textAlign: 'left' }}>
                          📊 Average MCAP
                        </Text>
                        
                        {/* Avg First MCAP */}
                        <div style={{ marginBottom: 8 }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Text size="xs" fw={500} c={theme.text.secondary}>First (USD)</Text>
                            <Tooltip label="Disable" position="top" withArrow>
                              <ActionIcon size={14} variant="subtle" color="red" onClick={() => setSelectedPreset({
                                ...selectedPreset,
                                min_avg_first_mcap_usd: null,
                                max_avg_first_mcap_usd: null
                              })}>
                                <IconBan size={10} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          <Group gap={4} wrap="nowrap">
                            <NumberInput
                              placeholder="min"
                              value={selectedPreset.min_avg_first_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                min_avg_first_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="max"
                              value={selectedPreset.max_avg_first_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                max_avg_first_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                        </div>

                        {/* Avg Last MCAP */}
                        <div style={{ marginBottom: 12 }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Text size="xs" fw={500} c={theme.text.secondary}>Last (USD)</Text>
                            <Tooltip label="Disable" position="top" withArrow>
                              <ActionIcon size={14} variant="subtle" color="red" onClick={() => setSelectedPreset({
                                ...selectedPreset,
                                min_avg_last_mcap_usd: null,
                                max_avg_last_mcap_usd: null
                              })}>
                                <IconBan size={10} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          <Group gap={4} wrap="nowrap">
                            <NumberInput
                              placeholder="min"
                              value={selectedPreset.min_avg_last_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                min_avg_last_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="max"
                              value={selectedPreset.max_avg_last_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                max_avg_last_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                        </div>

                        {/* Divider */}
                        <div style={{ height: 1, background: theme.border.primary, margin: '16px 0' }} />

                        {/* Median MCAP */}
                        <Text fw={600} size="sm" c={theme.text.primary} mb={8} style={{ textAlign: 'left' }}>
                          📈 Median MCAP
                        </Text>
                        
                        {/* Median First MCAP */}
                        <div style={{ marginBottom: 8 }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Text size="xs" fw={500} c={theme.text.secondary}>First (USD)</Text>
                            <Tooltip label="Disable" position="top" withArrow>
                              <ActionIcon size={14} variant="subtle" color="red" onClick={() => setSelectedPreset({
                                ...selectedPreset,
                                min_median_first_mcap_usd: null,
                                max_median_first_mcap_usd: null
                              })}>
                                <IconBan size={10} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          <Group gap={4} wrap="nowrap">
                            <NumberInput
                              placeholder="min"
                              value={selectedPreset.min_median_first_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                min_median_first_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="max"
                              value={selectedPreset.max_median_first_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                max_median_first_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                        </div>

                        {/* Median Last MCAP */}
                        <div style={{ marginBottom: 12 }}>
                          <Group mb={4} align="center" justify="space-between">
                            <Text size="xs" fw={500} c={theme.text.secondary}>Last (USD)</Text>
                            <Tooltip label="Disable" position="top" withArrow>
                              <ActionIcon size={14} variant="subtle" color="red" onClick={() => setSelectedPreset({
                                ...selectedPreset,
                                min_median_last_mcap_usd: null,
                                max_median_last_mcap_usd: null
                              })}>
                                <IconBan size={10} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                          <Group gap={4} wrap="nowrap">
                            <NumberInput
                              placeholder="min"
                              value={selectedPreset.min_median_last_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                min_median_last_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                            <NumberInput
                              placeholder="max"
                              value={selectedPreset.max_median_last_mcap_usd ?? ''}
                              onChange={(val) => setSelectedPreset({
                                ...selectedPreset,
                                max_median_last_mcap_usd: val === '' || val === null ? null : Number(val)
                              })}
                              min={0}
                              size="xs"
                              styles={{
                                root: { flex: 1 },
                                input: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.primary}`,
                                  color: theme.text.primary,
                                  minHeight: '28px',
                                  fontSize: '12px',
                                }
                              }}
                              hideControls
                            />
                          </Group>
                        </div>

                        <Text size="xs" c={theme.text.secondary} mb={12} style={{ textAlign: 'left' }}>
                          Filter wallets by average/median market cap at first and last trade
                        </Text>

                        {/* Divider */}
                        <div style={{ height: 1, background: theme.border.primary, margin: '16px 0' }} />

                        {/* Custom MCAP Bucket Filters - side by side */}
                        <Group mb={6} align="center" gap={6}>
                          <Text fw={600} size="sm" c={theme.text.primary} style={{ textAlign: 'left' }}>
                            🎯 Custom Buckets
                          </Text>
                          <ClickableHelpText 
                            text="Advanced MCAP filtering" 
                            detailedInfo="⚠️ Warning: Using bucket filters may significantly slow down export processing. Define MCAP ranges and specify what percentage of wallet's tokens should fall within each range. Example: bucket with MCAP 1k-10k and percent 10-100 means 10-100% of tokens should have MCAP in that range."
                          />
                        </Group>

                        <div style={{ display: 'flex', gap: 12 }}>
                          <McapBucketFilter
                            label="First MCAP"
                            helpText="At first trade"
                            detailedInfo="Filter by token MCAP at FIRST trade. Add buckets to define ranges."
                            value={selectedPreset.min_max_first_token_percent_mcap}
                            onChange={(buckets) => setSelectedPreset({
                              ...selectedPreset,
                              min_max_first_token_percent_mcap: buckets
                            })}
                            maxBuckets={3}
                          />

                          <McapBucketFilter
                            label="Last MCAP"
                            helpText="At last trade"
                            detailedInfo="Filter by token MCAP at LAST trade. Add buckets to define ranges."
                            value={selectedPreset.min_max_last_token_percent_mcap}
                            onChange={(buckets) => setSelectedPreset({
                              ...selectedPreset,
                              min_max_last_token_percent_mcap: buckets
                            })}
                            maxBuckets={3}
                          />
                        </div>
                      </div>
                    </Collapse>
                </>
              )}
              {/* Можно убрать нижний блок ошибок, если не нужен */}
              {/* {errors.length > 0 && (
                <Paper 
                  p="md" 
                  mt="md"
                  style={{
                    backgroundColor: `${theme.state.error}20`,
                    border: `1px solid ${theme.state.error}`,
                    borderRadius: '8px'
                  }}
                >
                  {errors.map((error, index) => (
                    <Text key={index} c={theme.state.error} size="sm">
                      {error}
                    </Text>
                  ))}
                </Paper>
              )} */}
            </div>
          )}
        </div>
      </div>
      {/* Sticky Save Preset Button */}
              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  width: '100%', // исправлено с 100vw
                  background: theme.background.secondary,
                  paddingTop: 16,
                  // marginLeft: -16,
                  // marginRight: -16,
                  // marginBottom: -16, // убрано
                  paddingLeft: 16,
                  paddingRight: 16,
                  boxShadow: '0 -4px 24px 0 rgba(0,0,0,0.12)',
                  // borderBottomLeftRadius: 12,
                  // borderBottomRightRadius: 12,
                }}
              >
                <Group justify="center">
                  <Button
                    leftSection={<IconCheck size={14} />} 
                    onClick={handleSavePreset}
                    size="sm"
                    fullWidth
                    styles={{
                      root: {
                        background: `linear-gradient(135deg, ${theme.accent.tertiary} 0%, #059669 100%)`,
                        border: 'none',
                        color: theme.text.primary,
                        fontWeight: 500,
                        minHeight: '40px',
                        marginBottom: '8px',
                        fontSize: '14px',
                        '&:hover': {
                          background: `linear-gradient(135deg, #047857 0%, #065F46 100%)`,
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  >
                    Save Preset
                  </Button>
                </Group>
              </div>
      </div>
      
      {/* Excel Settings Drawer */}
      <PeriodDaysExampleModal
        opened={periodExampleOpened}
        onClose={() => setPeriodExampleOpened(false)}
        periodDays={selectedPreset?.period_days}
      />
      <TokenSolInvestExampleModal
        opened={tokenSolInvestExampleOpened}
        onClose={() => setTokenSolInvestExampleOpened(false)}
        minSolTokenInvested={selectedPreset?.min_sol_token_invested}
      />
      <TradeTimeExampleModal
        opened={tradeTimeExampleMode != null}
        onClose={() => setTradeTimeExampleMode(null)}
        mode={tradeTimeExampleMode ?? 'last'}
        minSeconds={tradeTimeExampleMode === 'first'
          ? selectedPreset?.min_first_trade_time_ago
          : selectedPreset?.last_trade_time_ago_min}
        maxSeconds={tradeTimeExampleMode === 'first'
          ? selectedPreset?.max_first_trade_time_ago
          : selectedPreset?.last_trade_time_ago}
      />

      <Drawer
        opened={excelDrawerOpened}
        onClose={() => setExcelDrawerOpened(false)}
        title="Excel Style Settings"
        position="right"
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
        styles={{
          title: { 
            fontWeight: 700, 
            fontSize: 20, 
            color: theme.accent.primary 
          },
          body: { 
            background: theme.background.primary, 
            color: theme.text.primary,
            padding: 20
          },
          header: {
            background: theme.background.secondary,
            borderBottom: `1px solid ${theme.border.primary}`
          }
        }}
      >
        <ExcelStyleSettingsDrawer 
          settings={excelSettings}
          periodDays={selectedPreset?.period_days}
          loading={excelSettingsLoading}
          onSave={handleSaveExcelSettings}
        />
      </Drawer>
    </MantineProvider>
  );
};

export default DeepPresets;
